import { useState, type FormEvent } from 'react'
import { Loader2 } from 'lucide-react'
import { useAuth } from '../store/auth'
import { Button } from './ui'
import { AppFooter } from './AppFooter'

const field = 'rounded-lg border px-3 py-2 outline-none focus:border-[var(--color-accent)]'
const fieldStyle = { backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-line)', color: 'var(--color-ink)' } as const

/**
 * Erst-Einrichtung: Der erste Nutzer legt Familie + eigenes Admin-Konto an.
 * Danach übernimmt er die Benutzerverwaltung in den Einstellungen.
 */
export function SetupScreen(): React.JSX.Element {
  const { setup, error } = useAuth()
  const [familyName, setFamilyName] = useState('')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [birthdate, setBirthdate] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault()
    setBusy(true)
    await setup({ familyName: familyName.trim() || undefined, name: name.trim(), email: email.trim(), birthdate, password })
    setBusy(false)
  }

  const valid = name.trim() && /^\S+@\S+\.\S+$/.test(email) && /^\d{4}-\d{2}-\d{2}$/.test(birthdate) && password.length >= 6

  return (
    <main className="flex min-h-full items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <img src="/logo2.png" alt="FamilyNest" className="w-72" />
          <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
            Willkommen! Richte dein Familien-Konto ein.
          </p>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-3 rounded-[var(--radius-card)] border p-6" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }}>
          <Labeled label="Familienname (optional)">
            <input className={field} style={fieldStyle} value={familyName} onChange={(e) => setFamilyName(e.target.value)} placeholder="z. B. Familie Rehn" />
          </Labeled>
          <Labeled label="Dein Vorname">
            <input className={field} style={fieldStyle} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </Labeled>
          <Labeled label="E-Mail">
            <input type="email" className={field} style={fieldStyle} value={email} onChange={(e) => setEmail(e.target.value)} />
          </Labeled>
          <Labeled label="Geburtsdatum">
            <input type="date" className={field} style={fieldStyle} value={birthdate} onChange={(e) => setBirthdate(e.target.value)} />
          </Labeled>
          <Labeled label="Passwort (mind. 6 Zeichen)">
            <input type="password" className={field} style={fieldStyle} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
          </Labeled>

          {error && <p className="text-sm" style={{ color: 'var(--color-danger)' }}>{error}</p>}

          <Button type="submit" disabled={busy || !valid}>
            {busy && <Loader2 className="size-4 animate-spin" />}
            Konto erstellen & loslegen
          </Button>
        </form>
        <AppFooter />
      </div>
    </main>
  )
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span style={{ color: 'var(--color-muted)' }}>{label}</span>
      {children}
    </label>
  )
}
